import httpService from "../HttpService";
import {getCompanies} from "./CompanyService";
import {systemCompanyGroupAdminControllerCheckAvailability} from "../../api/generated";
import type {UserAvailability} from "../../api/generated/types.gen";

// Company Group Administrators — a SYSTEM_ADMIN-scoped persona.
//
// A "Company Group Administrator" (מנהל קבוצת חברות) is NOT tied to a single company. Instead they
// hold the distinct COMPANY_GROUP_ADMIN role and may manage/impersonate a curated set of existing
// companies. Backed by the server's SystemCompanyGroupAdminController (/system/companyGroupAdmin/*),
// which is SYSTEM_ADMIN-only (the super-admin creates and lists these personas).
//
// TODO(openapi-migration): `checkCompanyGroupAdminAvailability` goes through the generated SDK; the
// rest of this module still uses raw HttpService. Every one has a generated counterpart
// (systemCompanyGroupAdminControllerGetAll/Get/Create/Update/…), but switching them means
// re-verifying all 5 call sites, so that is left for a deliberate migration pass.

// Paged list with keyword (name/email) + enabled filtering. The server returns a Spring Page, so the
// shape is { data: { content, totalElements, totalPages } } (each row already carries its
// companies: [{ id, name }]).
export async function getCompanyGroupAdminsPaged(
  page: number = 0,
  size: number = 20,
  keyword: string | null = null,
  enabled: boolean | null = null,
) {
  const token = localStorage.getItem("auth");
  const params = new URLSearchParams();
  params.append("page", String(page));
  params.append("size", String(size));
  if (keyword) params.append("keyword", keyword);
  if (enabled !== null && enabled !== undefined) params.append("enabled", String(enabled));
  return httpService.get(`system/companyGroupAdmin?${params.toString()}`, null, token);
}

// KPI stats: { data: { total, active, inactive } }.
export async function getCompanyGroupAdminStats() {
  const token = localStorage.getItem("auth");
  return httpService.get("system/companyGroupAdmin/stats", null, token);
}

// Single group admin for the profile screen. Server validates the id resolves to a COMPANY_GROUP_ADMIN
// (never an arbitrary user); resolves { data: { id, name, email, phoneNumber, enabled, createdDate,
// createdByType, createdByIdentifier, companies: [{ id, name }] } }.
export async function getCompanyGroupAdmin(id: number) {
  const token = localStorage.getItem("auth");
  return httpService.get(`system/companyGroupAdmin/${id}`, null, token);
}

// Companies that a group admin can be authorized to impersonate — feeds the create-dialog picker.
// Reuses the cross-company list (SYSTEM_ADMIN scope); resolves { data: [{ id, name, ... }] }.
export async function getImpersonatableCompanies() {
  return getCompanies();
}

/** What the availability check answers: one independent flag per identifier that was submitted. */
export type AvailabilityResult = {emailExists: boolean; phoneExists: boolean; nameExists: boolean};

/** Profile edit payload. A null/absent `password` leaves the current one unchanged. */
export type UpdateGroupAdminPayload = {
  name?: string;
  email?: string;
  phoneNumber?: string;
  password?: string | null;
};

/** Create payload — a group admin is company-less, so its reach is only `companyIds`. */
export type CreateGroupAdminPayload = {
  name: string;
  email: string;
  phoneNumber: string;
  password: string;
  companyIds?: number[];
};

/**
 * Pre-flight "is this email / phone / name already taken?" check backing the create/edit form's
 * on-blur validation. Only the fields actually supplied are checked, and `excludeUserId` lets the
 * edit flow ignore the user's own current identifiers.
 *
 * The server normalizes the phone before looking it up, so a number typed in local form
 * (`050-123-4567`) still collides with the stored canonical `+972501234567` — precisely the case a
 * client-side-only check would miss and then fail on submit. `name` is a group-admin display-name
 * uniqueness rule (scoped to the persona, case-insensitive) — the server enforces the same on save.
 *
 * Returns the flat `{emailExists, phoneExists, nameExists}` body with each flag defaulted to `false`:
 * a flag the server omitted means "no collision", never "unknown".
 */
export async function checkCompanyGroupAdminAvailability(
  {email, phoneNumber, name, excludeUserId}: {
    email?: string | null;
    phoneNumber?: string | null;
    name?: string | null;
    excludeUserId?: number | null;
  },
): Promise<AvailabilityResult> {
  const query: {email?: string; phoneNumber?: string; name?: string; excludeUserId?: number} = {};
  if (email) query.email = email;
  if (phoneNumber) query.phoneNumber = phoneNumber;
  if (name) query.name = name;
  if (excludeUserId !== null && excludeUserId !== undefined) query.excludeUserId = excludeUserId;

  const {data, error} = await systemCompanyGroupAdminControllerCheckAvailability({query});
  if (error) throw error;
  const body: UserAvailability = data ?? {};
  return {
    emailExists: body.emailExists === true,
    phoneExists: body.phoneExists === true,
    nameExists: body.nameExists === true,
  };
}

// Edit a group admin's profile (name/email/phoneNumber) and, optionally, (re)set the password.
// Omitting `password` (or sending null) leaves the current password unchanged — the "change password"
// action sends only { password }. Resolves the updated { data: CompanyGroupAdmin }.
export async function updateCompanyGroupAdmin(id: number, payload: UpdateGroupAdminPayload) {
  const token = localStorage.getItem("auth");
  const body = {
    name: payload.name,
    email: payload.email,
    phoneNumber: payload.phoneNumber,
    password: payload.password ?? null,
  };
  return httpService.put(`system/companyGroupAdmin/${id}`, body, token);
}

// Block/unblock (disable/enable) a group admin's login. Never deletes.
export async function enableCompanyGroupAdmin(id: number) {
  const token = localStorage.getItem("auth");
  return httpService.put(`system/companyGroupAdmin/${id}/enable`, null, token);
}

export async function disableCompanyGroupAdmin(id: number) {
  const token = localStorage.getItem("auth");
  return httpService.put(`system/companyGroupAdmin/${id}/disable`, null, token);
}

// Replace the full set of companies this admin may impersonate (add + remove in one call).
export async function updateCompanyGroupAdminCompanies(id: number, companyIds: number[]) {
  const token = localStorage.getItem("auth");
  const body = {companyIds: Array.isArray(companyIds) ? companyIds : []};
  return httpService.put(`system/companyGroupAdmin/${id}/companies`, body, token);
}

// Soft-delete a group admin (frees its email/phone for reuse).
export async function deleteCompanyGroupAdmin(id: number) {
  const token = localStorage.getItem("auth");
  return httpService.delete(`system/companyGroupAdmin/${id}`, null, token);
}

// Create a new Company Group Administrator. Sends only the fields the server expects
// ({ name, email, phoneNumber, password, companyIds }); a group admin is never tied to a company
// and only brand-new users are supported (no existing-user promotion).
export async function createCompanyGroupAdmin(payload: CreateGroupAdminPayload) {
  const token = localStorage.getItem("auth");
  const body = {
    name: payload.name,
    email: payload.email,
    phoneNumber: payload.phoneNumber,
    password: payload.password,
    companyIds: Array.isArray(payload.companyIds) ? payload.companyIds : [],
  };
  return httpService.post("system/companyGroupAdmin", body, token);
}
